'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Toaster, toast } from 'sonner';
import { MapPin, Phone, Globe, Mail, Heart, GitCompare, Share2, ChevronRight, CheckCircle, Plus, Clock, Search, ChevronDown, ChevronUp, Navigation, ArrowRight, ExternalLink, X } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { SCHOOLS, khdaBannerColor } from '@/data/schools';
import type { School } from '@/data/schools';
import KhdaBadge from '@/components/ui/KhdaBadge';
import CurriculumBadge from '@/components/ui/CurriculumBadge';
import AppImage from '@/components/ui/AppImage';
import FeeBreakdownTable from './FeeBreakdownTable';
import { getClosestCommunitiesWithPricesAll, formatCommuteTime } from '@/data/communityPrices';
import { DUBAI_COMMUNITIES } from '@/data/schools';
import { toSlug } from '@/lib/seo/slugs';
import { trackSchoolProfileView, trackAddToShortlist, trackRemoveFromShortlist, trackCompareSchools, trackCommuteCalculated, trackAdmissionsClick, trackPhoneClick, trackEmailClick,  } from '@/lib/analytics/ga4Events';

const STORAGE_KEY = 'schoolmapper_compare_list';
const MAX_COMPARE = 10;

// Desktop tabs
const DESKTOP_TABS = [
  { id: 'tab-overview', label: 'Overview' },
  { id: 'tab-fees', label: 'Fees' },
  { id: 'tab-location', label: 'Location & Commute' },
  { id: 'tab-admissions', label: 'Admissions' },
];

// Mobile tabs — short labels to prevent overlap at 320–430px
const MOBILE_TABS = [
  { id: 'tab-overview', label: 'Overview' },
  { id: 'tab-fees', label: 'Fees' },
  { id: 'tab-location', label: 'Commute' },
  { id: 'tab-contact', label: 'Contact' },
];

interface SchoolProfileClientProps {
  schoolId: string;
  compareIds: string[];
}

function SchoolWatermark({ color = '#1e3a5f' }: { color?: string }) {
  return (
    <svg
      viewBox="0 0 160 130"
      className="absolute right-0 bottom-0 h-full w-auto pointer-events-none select-none"
      style={{ opacity: 0.10 }}
      fill={color}
      aria-hidden="true"
    >
      <rect x="20" y="55" width="120" height="75" rx="3" />
      <rect x="55" y="30" width="50" height="50" rx="2" />
      <polygon points="80,5 45,35 115,35" />
      <rect x="78" y="2" width="3" height="18" />
      <polygon points="81,2 100,8 81,14" />
      <rect x="28" y="70" width="18" height="22" rx="2" fill="white" opacity="0.6" />
      <rect x="28" y="100" width="18" height="20" rx="2" fill="white" opacity="0.6" />
      <rect x="114" y="70" width="18" height="22" rx="2" fill="white" opacity="0.6" />
      <rect x="114" y="100" width="18" height="20" rx="2" fill="white" opacity="0.6" />
      <rect x="65" y="90" width="30" height="40" rx="15 15 0 0" fill="white" opacity="0.6" />
      <rect x="68" y="38" width="24" height="20" rx="12 12 0 0" fill="white" opacity="0.6" />
    </svg>
  );
}

function getCompareList(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return [];
}

function saveCompareList(list: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  window.dispatchEvent(new Event('schoolmapper_compare_updated'));
}

// Simple map placeholder using iframe embed
function SchoolMap({ lat, lng, schoolName }: { lat: number; lng: number; schoolName: string }) {
  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.05},${lat - 0.05},${lng + 0.05},${lat + 0.05}&layer=mapnik&marker=${lat},${lng}`;
  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden border border-border bg-muted/30">
      <iframe
        src={mapUrl}
        title={`Map showing location of ${schoolName}`}
        className="w-full h-full border-0"
        loading="lazy"
        style={{ minHeight: '280px' }}
      />
      <div className="absolute bottom-2 right-2">
        <a
          href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=14/${lat}/${lng}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 px-2 py-1 bg-white rounded-lg text-xs font-medium text-foreground shadow border border-border hover:bg-muted transition-colors"
        >
          <ExternalLink size={10} />
          Open map
        </a>
      </div>
    </div>
  );
}

export default function SchoolProfileClient({ schoolId }: SchoolProfileClientProps) {
  const resolvedSchoolId = schoolId || '';
  const school = resolvedSchoolId ? SCHOOLS.find((s) => s.id === resolvedSchoolId) ?? null : null;
  const [activeTab, setActiveTab] = useState('tab-overview');
  const [saved, setSaved] = useState(false);
  const [showAllCommunities, setShowAllCommunities] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState<string>('');
  const [communitySearch, setCommunitySearch] = useState('');
  const [showCommunityDropdown, setShowCommunityDropdown] = useState(false);
  const communityInputRef = useRef<HTMLInputElement>(null);

  // Track school_profile_view once per school — not on rerenders
  const profileViewFiredRef = useRef<string>('');
  useEffect(() => {
    if (!school) return;
    if (profileViewFiredRef.current === school.id) return;
    profileViewFiredRef.current = school.id;
    trackSchoolProfileView({
      school_name: school.name,
      school_slug: school.id,
      curriculum: school.curriculum,
      khda_rating: school.khdaRating,
    });
  }, [school]);

  // Track commute_calculated when user selects a community (user-initiated action only)
  const lastCommuteFiredRef = useRef<string>('');
  useEffect(() => {
    if (!school || !selectedCommunity) return;
    const key = `${school.id}::${selectedCommunity}`;
    if (lastCommuteFiredRef.current === key) return;
    lastCommuteFiredRef.current = key;

    const closestCommunities = getClosestCommunitiesWithPricesAll(school.coordinates.lat, school.coordinates.lng, 20);
    const communityData = closestCommunities.find(c => c.name === selectedCommunity);
    if (!communityData) return;

    trackCommuteCalculated({
      school_name: school.name,
      community_name: selectedCommunity,
      commute_minutes: communityData.travelMins,
      direction: 'community_to_school',
    });
  }, [selectedCommunity, school]);

  const handleCompareButtonClick = () => {
    if (!school) return;
    const current = getCompareList();
    if (current.includes(school.id)) {
      toast.info(`${school.shortName} is already in your comparison`);
      return;
    }
    if (current.length >= MAX_COMPARE) {
      toast.error(`You can compare up to ${MAX_COMPARE} schools`);
      return;
    }
    const next = [...current, school.id];
    saveCompareList(next);
    const schoolNames = next.map(id => SCHOOLS.find(s => s.id === id)?.name).filter(Boolean).join(', ');
    trackCompareSchools({ number_of_schools: next.length, school_names: schoolNames || undefined });
    toast.success(`${school.shortName} added to School Comparison`, {
      action: {
        label: 'View Comparison',
        onClick: () => { window.location.href = '/school-profile-comparison'; },
      },
    });
  };

  const handleAddSimilarToCompare = (id: string) => {
    const s = SCHOOLS.find((x) => x.id === id);
    if (!s) return;
    const current = getCompareList();
    if (current.includes(id)) {
      toast.info(`${s.shortName} is already in your comparison`);
      return;
    }
    if (current.length >= MAX_COMPARE) {
      toast.error(`You can compare up to ${MAX_COMPARE} schools`);
      return;
    }
    const next = [...current, id];
    saveCompareList(next);
    const schoolNames = next.map(sid => SCHOOLS.find(sc => sc.id === sid)?.name).filter(Boolean).join(', ');
    trackCompareSchools({ number_of_schools: next.length, school_names: schoolNames || undefined });
    toast.success(`${s.shortName} added to School Comparison`, {
      action: {
        label: 'View Comparison',
        onClick: () => { window.location.href = '/school-profile-comparison'; },
      },
    });
  };

  if (!school) {
    return (
      <>
        <Toaster position="bottom-right" richColors />
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-20 text-center">
          <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
            <GitCompare size={36} className="text-primary" />
          </div>
          <h2 className="font-display font-bold text-2xl text-foreground mb-3">No School Selected</h2>
          <p className="text-muted-foreground text-base max-w-md mb-8">
            Browse schools and click &ldquo;View Profile&rdquo; to see a school&apos;s full details.
          </p>
          <Link
            href="/school-search-discovery"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 transition-all active:scale-95"
          >
            <Search size={16} />
            Browse Schools
          </Link>
        </div>
      </>
    );
  }

  const bannerColor = khdaBannerColor(school.khdaRating);
  const closestCommunities = getClosestCommunitiesWithPricesAll(school.coordinates.lat, school.coordinates.lng, 20);
  const top8Communities = closestCommunities.slice(0, 8);
  const top5Communities = closestCommunities.slice(0, 5);

  // Community commute lookup
  const selectedCommunityData = selectedCommunity
    ? closestCommunities.find((c) => c.name === selectedCommunity) ?? null
    : null;

  const filteredCommunities = DUBAI_COMMUNITIES.filter((c) =>
    c.toLowerCase().includes(communitySearch.toLowerCase())
  ).slice(0, 8);

  function formatFee(n: number) {
    if (n === 0) return 'N/A';
    return 'AED ' + n.toLocaleString('en-AE');
  }

  function commuteColor(mins: number) {
    if (mins <= 15) return '#16a34a';
    if (mins <= 25) return '#0284c7';
    if (mins <= 35) return '#d97706';
    return '#dc2626';
  }

  return (
    <>
      <Toaster position="bottom-right" richColors />

      {/* ================================================================
          DESKTOP / TABLET (sm and above) — REDESIGNED premium layout
      ================================================================ */}
      <div className="hidden sm:block bg-gray-50 min-h-screen">

        {/* ── Compact Header ── */}
        <div className="bg-white border-b border-border shadow-sm">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
            <div className="py-4 flex items-start gap-4">
              {/* Logo */}
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
                style={{ backgroundColor: bannerColor + '15', border: `2px solid ${bannerColor}40` }}
              >
                <Image
                  src="/assets/images/image-1786254194769.png"
                  alt="SchoolMapper"
                  width={52}
                  height={52}
                  className="object-contain"
                  style={{ width: '52px', height: '52px' }}
                />
              </div>

              {/* School identity */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h1 className="font-bold text-xl lg:text-2xl leading-tight text-foreground" style={{ color: '#1e2d5a' }}>
                      {school.name}
                    </h1>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <KhdaBadge rating={school.khdaRating} size="sm" />
                      <CurriculumBadge curriculum={school.curriculum} />
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin size={11} />
                        {school.location}
                      </span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground">{school.ageRange}</span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground">{school.schoolType}</span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground">{school.genderPolicy}</span>
                    </div>
                    {/* Fee range — prominent */}
                    <div className="mt-2">
                      <span className="text-sm font-bold tabular-nums" style={{ color: bannerColor }}>
                        {school.feeRangeMin === 0 ? 'Non-fee paying' : `${formatFee(school.feeRangeMin)} – ${formatFee(school.feeRangeMax)}`}
                      </span>
                      <span className="text-xs text-muted-foreground ml-1.5">per year</span>
                      <button
                        onClick={() => setActiveTab('tab-fees')}
                        className="ml-3 text-xs text-primary font-medium hover:underline"
                      >
                        View fees by year group →
                      </button>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => {
                        const wasSaved = saved;
                        setSaved(!wasSaved);
                        toast[wasSaved ? 'info' : 'success'](wasSaved ? 'Removed from shortlist' : 'Added to your shortlist');
                        if (wasSaved) {
                          trackRemoveFromShortlist({ school_name: school.name, school_slug: school.id });
                        } else {
                          trackAddToShortlist({ school_name: school.name, school_slug: school.id });
                        }
                      }}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition-all duration-150 ${saved ? 'bg-red-50 border-red-200 text-red-600' : 'bg-white border-border text-muted-foreground hover:border-red-200 hover:text-red-500'}`}
                    >
                      <Heart size={14} className={saved ? 'fill-red-500' : ''} />
                      <span className="hidden lg:inline">Shortlist</span>
                    </button>
                    <button
                      onClick={() => {
                        if (typeof navigator !== 'undefined') navigator.clipboard?.writeText(window.location.href);
                        toast.success('Link copied to clipboard');
                      }}
                      className="p-2 rounded-xl border border-border bg-white text-muted-foreground hover:text-foreground transition-all duration-150"
                      title="Share"
                    >
                      <Share2 size={14} />
                    </button>
                    <button
                      onClick={handleCompareButtonClick}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-white text-sm font-medium text-foreground hover:border-primary/40 hover:text-primary transition-all duration-150"
                    >
                      <GitCompare size={14} />
                      Compare
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center gap-0 border-t border-border -mx-4 sm:-mx-6 lg:-mx-8 xl:-mx-10 px-4 sm:px-6 lg:px-8 xl:px-10 overflow-x-auto scrollbar-thin">
              {DESKTOP_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-150 ${
                    activeTab === tab.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Main Content: 70/30 layout ── */}
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6">
          <div className="flex gap-6 items-start">

            {/* ── Main Column (70%) ── */}
            <div className="flex-1 min-w-0 space-y-5">

              {/* OVERVIEW TAB */}
              {activeTab === 'tab-overview' && (
                <div className="space-y-5 animate-fade-in">

                  {/* At a Glance */}
                  <div className="bg-white rounded-2xl border border-border p-5 shadow-sm">
                    <h2 className="font-bold text-sm text-muted-foreground uppercase tracking-wider mb-4">At a Glance</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="rounded-xl p-3" style={{ backgroundColor: bannerColor + '10', border: `1px solid ${bannerColor}25` }}>
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">KHDA Rating</p>
                        <p className="text-sm font-bold" style={{ color: bannerColor }}>{school.khdaRating}</p>
                      </div>
                      <div className="rounded-xl p-3 bg-blue-50 border border-blue-100">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Curriculum</p>
                        <p className="text-sm font-bold text-blue-700">{school.curriculum}</p>
                      </div>
                      <div className="rounded-xl p-3 bg-muted/50 border border-border">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Age Range</p>
                        <p className="text-sm font-bold text-foreground">{school.ageRange}</p>
                      </div>
                      <div className="rounded-xl p-3 bg-muted/50 border border-border">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Gender</p>
                        <p className="text-sm font-bold text-foreground">{school.genderPolicy}</p>
                      </div>
                      <div className="rounded-xl p-3 bg-muted/50 border border-border">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">School Type</p>
                        <p className="text-sm font-bold text-foreground">{school.schoolType}</p>
                      </div>
                      <div className="rounded-xl p-3 bg-muted/50 border border-border">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Seats</p>
                        <p className="text-sm font-bold text-foreground">Contact school</p>
                      </div>
                      <div className="rounded-xl p-3 bg-emerald-50 border border-emerald-100 col-span-2">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Fee Range</p>
                        <p className="text-sm font-bold text-emerald-700 tabular-nums">
                          {school.feeRangeMin === 0 ? 'Non-fee paying' : `${formatFee(school.feeRangeMin)} – ${formatFee(school.feeRangeMax)}`}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* About */}
                  <div className="bg-white rounded-2xl border border-border p-5 shadow-sm">
                    <h2 className="font-bold text-base text-foreground mb-3">About {school.shortName}</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">{school.description}</p>
                    {school.highlights.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {school.highlights.map((h) => (
                          <span key={`highlight-${h}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/8 text-primary border border-primary/20 rounded-full text-xs font-medium">
                            <CheckCircle size={11} />
                            {h}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Location preview — teaser to drive to Location tab */}
                  <div
                    className="rounded-2xl border p-5 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                    style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)', borderColor: '#bbf7d0' }}
                    onClick={() => setActiveTab('tab-location')}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                          <Navigation size={15} className="text-white" />
                        </div>
                        <div>
                          <h2 className="font-bold text-sm text-emerald-900">Communities closest to this school</h2>
                          <p className="text-xs text-emerald-700">Peak drive times from nearby communities</p>
                        </div>
                      </div>
                      <button className="flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 transition-colors">
                        View all & check commute <ArrowRight size={12} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {top8Communities.slice(0, 4).map((c) => (
                        <div key={c.name} className="flex items-center justify-between bg-white/80 rounded-lg px-3 py-2 border border-emerald-100">
                          <span className="text-xs font-medium text-foreground truncate mr-2">{c.name}</span>
                          <span className="text-xs font-bold tabular-nums shrink-0" style={{ color: commuteColor(c.travelMins) }}>
                            ~{formatCommuteTime(c.travelMins)}m
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* FEES TAB */}
              {activeTab === 'tab-fees' && (
                <div className="space-y-5 animate-fade-in">
                  {/* Fee summary banner */}
                  <div className="rounded-2xl p-5 shadow-sm" style={{ background: `linear-gradient(135deg, ${bannerColor}12 0%, ${bannerColor}06 100%)`, border: `1px solid ${bannerColor}30` }}>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Annual Fee Range</p>
                    <p className="text-2xl font-bold tabular-nums" style={{ color: bannerColor }}>
                      {school.feeRangeMin === 0 ? 'Non-fee paying' : `${formatFee(school.feeRangeMin)} – ${formatFee(school.feeRangeMax)}`}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Fees vary by year group. Contact the school for the latest schedule.</p>
                  </div>
                  <div className="bg-white rounded-2xl border border-border p-5 shadow-sm">
                    <FeeBreakdownTable
                      fees={school.fees}
                      applicationFeeAed={school.applicationFeeAed}
                      admissionTestRequired={school.admissionTestRequired}
                      isMobile={false}
                    />
                  </div>
                </div>
              )}

              {/* LOCATION & COMMUTE TAB */}
              {activeTab === 'tab-location' && (
                <div className="space-y-5 animate-fade-in">
                  {/* Hero location section */}
                  <div className="bg-white rounded-2xl border border-border overflow-hidden shadow-sm">
                    <div className="px-5 py-4 border-b border-border" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)' }}>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center">
                          <Navigation size={13} className="text-white" />
                        </div>
                        <h2 className="font-bold text-base text-emerald-900">Communities closest to this school</h2>
                      </div>
                      <p className="text-xs text-emerald-700 ml-9">SchoolMapper&apos;s location intelligence — find where to live to be close to this school</p>
                    </div>

                    {/* Two-column: map + communities */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                      {/* Map */}
                      <div className="p-4 border-b lg:border-b-0 lg:border-r border-border" style={{ minHeight: '320px' }}>
                        <SchoolMap lat={school.coordinates.lat} lng={school.coordinates.lng} schoolName={school.name} />
                      </div>

                      {/* Communities list */}
                      <div className="p-4">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Nearest communities by drive time</p>
                        <div className="space-y-1.5">
                          {(showAllCommunities ? closestCommunities : top8Communities).map((c, idx) => (
                            <div key={c.name} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/30 transition-colors">
                              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0">{idx + 1}</span>
                              <span className="text-sm text-foreground flex-1 truncate">{c.name}</span>
                              <span
                                className="text-xs font-bold tabular-nums shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full"
                                style={{ color: commuteColor(c.travelMins), backgroundColor: commuteColor(c.travelMins) + '15' }}
                              >
                                <Clock size={10} />
                                ~{formatCommuteTime(c.travelMins)} min
                              </span>
                            </div>
                          ))}
                        </div>
                        {closestCommunities.length > 8 && (
                          <button
                            onClick={() => setShowAllCommunities(!showAllCommunities)}
                            className="mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors rounded-xl border border-emerald-200"
                          >
                            {showAllCommunities ? (
                              <><ChevronUp size={14} /> Show fewer communities</>
                            ) : (
                              <><ChevronDown size={14} /> View all {closestCommunities.length} communities</>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Community commute search */}
                  <div className="bg-white rounded-2xl border border-border p-5 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center">
                        <Search size={13} className="text-white" />
                      </div>
                      <div>
                        <h2 className="font-bold text-base text-foreground">Check your commute</h2>
                        <p className="text-xs text-muted-foreground">Where do you live?</p>
                      </div>
                    </div>

                    {selectedCommunity ? (
                      <div className="rounded-xl p-4 border" style={{ backgroundColor: commuteColor(selectedCommunityData?.travelMins ?? 30) + '10', borderColor: commuteColor(selectedCommunityData?.travelMins ?? 30) + '40' }}>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-xs text-muted-foreground font-medium mb-1">Your commute</p>
                            <p className="font-bold text-lg text-foreground">{selectedCommunity} → {school.shortName}</p>
                            <p className="text-2xl font-bold mt-1 tabular-nums" style={{ color: commuteColor(selectedCommunityData?.travelMins ?? 30) }}>
                              ~{selectedCommunityData ? formatCommuteTime(selectedCommunityData.travelMins) : '?'} min drive
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">Estimated peak traffic drive time</p>
                          </div>
                          <button
                            onClick={() => { setSelectedCommunity(''); setCommunitySearch(''); }}
                            className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <button
                          onClick={() => { setSelectedCommunity(''); setCommunitySearch(''); }}
                          className="mt-3 text-xs font-semibold text-primary hover:underline"
                        >
                          Change community
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="relative">
                          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <input
                            ref={communityInputRef}
                            type="text"
                            placeholder="Search for your community..."
                            value={communitySearch}
                            onChange={(e) => { setCommunitySearch(e.target.value); setShowCommunityDropdown(true); }}
                            onFocus={() => setShowCommunityDropdown(true)}
                            className="w-full pl-9 pr-4 py-3 rounded-xl border border-border bg-muted/30 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                          />
                        </div>
                        {showCommunityDropdown && communitySearch.length > 0 && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-xl shadow-lg z-20 overflow-hidden max-h-48 overflow-y-auto">
                            {filteredCommunities.length > 0 ? filteredCommunities.map((c) => (
                              <button
                                key={c}
                                onClick={() => { setSelectedCommunity(c); setCommunitySearch(''); setShowCommunityDropdown(false); }}
                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0"
                              >
                                {c}
                              </button>
                            )) : (
                              <p className="px-4 py-3 text-sm text-muted-foreground">No communities found</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Reverse search CTA */}
                  <div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="font-bold text-sm text-foreground mb-1">Find schools near another community</h3>
                        <p className="text-xs text-muted-foreground">SchoolMapper works both ways — discover schools from any community, or find communities near any school.</p>
                      </div>
                      <Link
                        href="/school-search-discovery"
                        className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                        style={{ backgroundColor: '#16a34a' }}
                      >
                        <Search size={13} />
                        Search by community
                      </Link>
                    </div>
                  </div>

                  {/* SEO internal link: Communities Near This School */}
                  <div className="rounded-2xl border border-border bg-primary/5 p-4 shadow-sm">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="font-bold text-sm text-foreground mb-1">Communities near {school.shortName}</h3>
                        <p className="text-xs text-muted-foreground">See which Dubai communities are within a convenient commute of this school.</p>
                      </div>
                      <Link
                        href={`/communities-near/${toSlug(school.name)}`}
                        className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                        style={{ backgroundColor: '#1e3a5f' }}
                      >
                        <Navigation size={13} />
                        View communities
                      </Link>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="bg-white rounded-2xl border border-border p-5 shadow-sm">
                    <h3 className="font-bold text-sm text-foreground mb-3">School Address</h3>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center shrink-0">
                        <MapPin size={14} className="text-muted-foreground" />
                      </div>
                      <p className="text-sm text-foreground">{school.address}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* ADMISSIONS TAB */}
              {activeTab === 'tab-admissions' && (
                <div className="space-y-5 animate-fade-in">
                  <div className="bg-white rounded-2xl border border-border p-5 shadow-sm">
                    <h2 className="font-bold text-base text-foreground mb-4">Admissions Information</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="rounded-xl p-4 bg-muted/40 border border-border">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Application Status</p>
                        <p className="text-sm font-bold text-foreground">{school.applicationStatus}</p>
                      </div>
                      <div className="rounded-xl p-4 bg-muted/40 border border-border">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Application Deadline</p>
                        <p className="text-sm font-bold text-foreground">{school.applicationDeadline || 'Contact school'}</p>
                      </div>
                      <div className="rounded-xl p-4 bg-muted/40 border border-border">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Year Groups Offered</p>
                        <p className="text-sm font-bold text-foreground">{school.yearGroupsOffered?.join(', ') || school.ageRange}</p>
                      </div>
                      <div className="rounded-xl p-4 bg-muted/40 border border-border">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Seat Availability</p>
                        <p className="text-sm font-bold text-foreground">Contact school directly</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-border p-5 shadow-sm">
                    <h3 className="font-bold text-sm text-foreground mb-4">Contact Admissions</h3>
                    <div className="space-y-3">
                      {school.phone && (
                        <a href={`tel:${school.phone}`} onClick={() => trackPhoneClick({ school_name: school.name })} className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all group">
                          <div className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center shrink-0">
                            <Phone size={14} className="text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground font-medium">Phone</p>
                            <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{school.phone}</p>
                          </div>
                        </a>
                      )}
                      {school.email && (
                        <a href={`mailto:${school.email}`} onClick={() => trackEmailClick({ school_name: school.name })} className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all group">
                          <div className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center shrink-0">
                            <Mail size={14} className="text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs text-muted-foreground font-medium">Admissions Email</p>
                            <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate">{school.email}</p>
                          </div>
                        </a>
                      )}
                      {school.admissionsWebsite && (
                        <a href={school.admissionsWebsite} target="_blank" rel="noopener noreferrer" onClick={() => trackAdmissionsClick({ school_name: school.name, destination_url: school.admissionsWebsite! })} className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all group">
                          <div className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center shrink-0">
                            <Globe size={14} className="text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground font-medium">Admissions Website</p>
                            <p className="text-sm font-semibold text-primary">Visit admissions page →</p>
                          </div>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Sticky Sidebar (30%) ── */}
            <div className="w-72 xl:w-80 shrink-0 space-y-4" style={{ position: 'sticky', top: '80px', alignSelf: 'flex-start' }}>

              {/* CTA Card */}
              <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-border" style={{ backgroundColor: bannerColor }}>
                  <p className="text-white font-bold text-sm">Interested in this school?</p>
                  <p className="text-white/80 text-xs mt-0.5">Take the next step</p>
                </div>
                <div className="p-4 space-y-2.5">
                  {school.phone && (
                    <a
                      href={`tel:${school.phone}`}
                      onClick={() => trackPhoneClick({ school_name: school.name })}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                      style={{ backgroundColor: bannerColor }}
                    >
                      <Phone size={14} />
                      Contact School
                    </a>
                  )}
                  {school.admissionsWebsite && (
                    <a
                      href={school.admissionsWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackAdmissionsClick({ school_name: school.name, destination_url: school.admissionsWebsite! })}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border border-border text-foreground hover:border-primary/40 hover:text-primary transition-all"
                    >
                      <ExternalLink size={14} />
                      Visit Admissions Page
                    </a>
                  )}
                  <button
                    onClick={() => {
                      const wasSaved = saved;
                      setSaved(!wasSaved);
                      toast[wasSaved ? 'info' : 'success'](wasSaved ? 'Removed from shortlist' : 'Added to your shortlist');
                      if (wasSaved) {
                        trackRemoveFromShortlist({ school_name: school.name, school_slug: school.id });
                      } else {
                        trackAddToShortlist({ school_name: school.name, school_slug: school.id });
                      }
                    }}
                    className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border transition-all ${saved ? 'bg-red-50 border-red-200 text-red-600' : 'border-border text-foreground hover:border-red-200 hover:text-red-500'}`}
                  >
                    <Heart size={14} className={saved ? 'fill-red-500' : ''} />
                    {saved ? 'Remove from Shortlist' : 'Add to Shortlist'}
                  </button>
                  <button
                    onClick={handleCompareButtonClick}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border border-border text-foreground hover:border-primary/40 hover:text-primary transition-all"
                  >
                    <GitCompare size={14} />
                    Compare Schools
                  </button>
                  <button
                    onClick={() => setActiveTab('tab-location')}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-all"
                  >
                    <Navigation size={14} />
                    Check Commute
                  </button>
                </div>
              </div>

              {/* Contact info */}
              <div className="bg-white rounded-2xl border border-border p-4 shadow-sm">
                <h4 className="font-bold text-xs text-muted-foreground uppercase tracking-wide mb-3">Contact</h4>
                <div className="space-y-2">
                  {school.phone && (
                    <a href={`tel:${school.phone}`} onClick={() => trackPhoneClick({ school_name: school.name })} className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors group">
                      <Phone size={13} className="text-muted-foreground group-hover:text-primary shrink-0" />
                      <span className="truncate">{school.phone}</span>
                    </a>
                  )}
                  {school.email && (
                    <a href={`mailto:${school.email}`} onClick={() => trackEmailClick({ school_name: school.name })} className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors group">
                      <Mail size={13} className="text-muted-foreground group-hover:text-primary shrink-0" />
                      <span className="truncate text-xs">{school.email}</span>
                    </a>
                  )}
                  {school.address && (
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <MapPin size={13} className="shrink-0 mt-0.5" />
                      <span className="text-xs leading-relaxed">{school.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Similar Schools */}
              <div className="bg-white rounded-2xl border border-border p-4 shadow-sm">
                <h4 className="font-bold text-xs text-muted-foreground uppercase tracking-wide mb-3">Similar Schools</h4>
                <div className="space-y-2">
                  {SCHOOLS.filter((s) => s.id !== school.id && s.curriculum === school.curriculum).slice(0, 3).map((similar) => (
                    <Link key={`similar-${similar.id}`} href={`/school-profile-comparison?school=${similar.id}`} className="flex items-center gap-3 p-2.5 rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all group">
                      <div className="w-9 h-9 rounded-lg overflow-hidden bg-muted shrink-0">
                        <AppImage src={similar.logoUrl} alt={`${similar.shortName} logo`} width={36} height={36} unoptimized />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-foreground truncate group-hover:text-primary transition-colors">{similar.shortName}</p>
                        <KhdaBadge rating={similar.khdaRating} size="sm" showIcon={false} />
                      </div>
                      <ChevronRight size={12} className="text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                    </Link>
                  ))}
                </div>
                <button
                  onClick={() => {
                    const firstSimilar = SCHOOLS.find((s) => s.id !== school.id && s.curriculum === school.curriculum);
                    if (firstSimilar) handleAddSimilarToCompare(firstSimilar.id);
                  }}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 border-2 border-dashed border-border rounded-xl text-xs text-muted-foreground hover:border-primary/40 hover:text-primary transition-all"
                >
                  <Plus size={12} />
                  Add to Comparison
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================
          MOBILE (below sm) — Premium redesigned layout
      ================================================================ */}
      <div className="sm:hidden">
        {/* ── Hero Summary ── */}
        <div className="bg-white border-b border-border px-4 pt-4 pb-4">
          {/* School name + actions row */}
          <div className="flex items-start gap-3 mb-3">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
              style={{ backgroundColor: bannerColor + '18', border: `2px solid ${bannerColor}40` }}
            >
              <Image
                src="/assets/images/image-1786254194769.png"
                alt="SchoolMapper"
                width={44}
                height={44}
                className="object-contain"
                style={{ width: '44px', height: '44px' }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-bold text-lg leading-tight" style={{ color: '#1e2d5a' }}>{school.name}</h1>
              <p className="text-xs font-semibold mt-0.5" style={{ color: bannerColor }}>{school.schoolType}</p>
            </div>
            {/* Action icons */}
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => {
                  setSaved(!saved);
                  toast[saved ? 'info' : 'success'](saved ? 'Removed from shortlist' : 'Added to your shortlist');
                }}
                className={`p-2.5 rounded-xl border transition-all duration-150 active:scale-90 ${saved ? 'bg-red-50 border-red-200 text-red-500' : 'bg-white border-border text-muted-foreground'}`}
              >
                <Heart size={15} className={saved ? 'fill-red-500' : ''} />
              </button>
              <button
                onClick={() => {
                  if (typeof navigator !== 'undefined') navigator.clipboard?.writeText(window.location.href);
                  toast.success('Link copied');
                }}
                className="p-2.5 rounded-xl border border-border bg-white text-muted-foreground"
              >
                <Share2 size={15} />
              </button>
            </div>
          </div>

          {/* Key info pills */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold"
              style={{ backgroundColor: bannerColor + '18', color: bannerColor, border: `1px solid ${bannerColor}40` }}
            >
              <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor" aria-hidden="true"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>
              {school.khdaRating}
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
              {school.curriculum}
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">
              {school.ageRange}
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">
              <MapPin size={10} />
              {school.location}
            </span>
          </div>

          {/* Fee range — prominent */}
          <div className="rounded-xl px-3 py-2.5 mb-3" style={{ backgroundColor: bannerColor + '10', border: `1px solid ${bannerColor}30` }}>
            <p className="text-xs text-muted-foreground font-medium mb-0.5">Annual Fee Range</p>
            <p className="text-base font-bold tabular-nums" style={{ color: bannerColor }}>
              {school.feeRangeMin === 0 ? 'Non-fee paying' : `${formatFee(school.feeRangeMin)} – ${formatFee(school.feeRangeMax)}`}
            </p>
          </div>

          {/* ── Check Your Commute module ── */}
          <div className="rounded-xl overflow-hidden mb-3" style={{ border: '1.5px solid #bbf7d0', background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)' }}>
            <div className="px-4 py-3 border-b border-border" style={{ backgroundColor: '#f0fdf4' }}>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <Navigation size={13} className="text-white" />
                </div>
                <h2 className="font-bold text-base text-emerald-900">Check Your Commute</h2>
              </div>
              <p className="text-xs text-emerald-700">Enter your current location to estimate your drive time to this school.</p>
            </div>
            <div className="p-4">
              <div className="space-y-2">
                <div className="rounded-xl p-3 bg-muted/30 border border-border">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center shrink-0">
                      <MapPin size={14} className="text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground font-medium">Your Location</p>
                      <p className="text-sm text-foreground">{school.address}</p>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl p-3 bg-white border border-border">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center shrink-0">
                      <Navigation size={14} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground font-medium">{school.shortName}</p>
                      <p className="text-sm text-muted-foreground">{school.location}</p>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl p-3 bg-muted/30 border border-border">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center shrink-0">
                      <Clock size={14} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground font-medium">Drive Time</p>
                      <p className="text-sm text-foreground">{formatCommuteTime(selectedCommunityData?.travelMins ?? 30)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}